import {
  Directive,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  KeyValueChanges,
  KeyValueDiffer,
  KeyValueDiffers,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import PerfectScrollbar from 'perfect-scrollbar';
import { InjectionToken } from '@angular/core';

/**
 * Local replacement for ngx-perfect-scrollbar's PerfectScrollbarDirective.
 *
 * ngx-perfect-scrollbar was published for ViewEngine and its last release (10.1.1) is
 * npm-deprecated, so Angular 16 -- which removes ngcc -- cannot consume it at all. The
 * vanilla `perfect-scrollbar` package it wraps is already a direct dependency of this
 * app, so the wrapper is reproduced here rather than swapping to a different scrollbar.
 *
 * This is a transplant of the original directive's logic, not a reimplementation: the
 * config merge, the `disabled` handling, the ResizeObserver-driven update, the
 * outside-zone execution and the debounced update all behave as before. The parts the
 * app never used are deliberately absent -- the ten psScroll and psReach outputs, and
 * the ps(), geometry(), position() and scrollTo methods (nothing takes a reference to
 * this directive).
 */
export const PERFECT_SCROLLBAR_CONFIG = new InjectionToken<PerfectScrollbarConfigInterface>(
  'PERFECT_SCROLLBAR_CONFIG'
);

export type PerfectScrollbarConfigInterface = PerfectScrollbar.Options;

@Directive({
  selector: '[perfectScrollbar]',
  exportAs: 'ngxPerfectScrollbar',
  standalone: true,
})
export class PerfectScrollbarDirective implements OnInit, OnDestroy, DoCheck, OnChanges {
  @Input('perfectScrollbar') config?: PerfectScrollbarConfigInterface;
  @Input() disabled = false;

  private instance: PerfectScrollbar | null = null;
  private ro: ResizeObserver | null = null;
  private timeout: number | null = null;
  private configDiff: KeyValueDiffer<string, unknown> | null = null;

  constructor(
    private zone: NgZone,
    private differs: KeyValueDiffers,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(PERFECT_SCROLLBAR_CONFIG) private defaults: PerfectScrollbarConfigInterface
  ) {}

  ngOnInit(): void {
    if (this.disabled || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const overrides = this.overrides();
    const config = { ...(this.defaults || {}), ...overrides } as PerfectScrollbarConfigInterface;

    this.zone.runOutsideAngular(() => {
      this.instance = new PerfectScrollbar(this.elementRef.nativeElement, config);
    });

    if (!this.configDiff) {
      this.configDiff = this.differs.find(overrides).create();
      this.configDiff.diff(overrides);
    }

    this.zone.runOutsideAngular(() => {
      this.ro = new ResizeObserver(() => this.update());
      const firstChild = this.elementRef.nativeElement.children[0];
      if (firstChild) {
        this.ro.observe(firstChild);
      }
      this.ro.observe(this.elementRef.nativeElement);
    });
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }
    if (this.timeout !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.zone.runOutsideAngular(() => {
      if (this.instance) {
        this.instance.destroy();
      }
    });
    this.instance = null;
  }

  ngDoCheck(): void {
    if (this.disabled || !this.configDiff || !isPlatformBrowser(this.platformId)) {
      return;
    }
    const changes: KeyValueChanges<string, unknown> | null = this.configDiff.diff(this.overrides());
    if (changes) {
      this.ngOnDestroy();
      this.ngOnInit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const disabledChange = changes['disabled'];
    if (!disabledChange || disabledChange.isFirstChange() || !isPlatformBrowser(this.platformId)) {
      return;
    }
    if (disabledChange.currentValue === disabledChange.previousValue) {
      return;
    }
    if (disabledChange.currentValue === true) {
      this.ngOnDestroy();
    } else {
      this.ngOnInit();
    }
  }

  /**
   * Templates bind `[perfectScrollbar]=""`, so the input can arrive as an empty string
   * rather than an object. The KeyValueDiffer also needs an indexable view, which
   * PerfectScrollbar.Options is not.
   */
  private overrides(): { [key: string]: unknown } {
    return typeof this.config === 'object' && this.config
      ? (this.config as unknown as { [key: string]: unknown })
      : {};
  }

  private update(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.timeout !== null) {
      window.clearTimeout(this.timeout);
    }
    this.timeout = window.setTimeout(() => {
      if (this.disabled || !this.configDiff) {
        return;
      }
      try {
        this.zone.runOutsideAngular(() => {
          if (this.instance) {
            this.instance.update();
          }
        });
      } catch (error) {
        // An update can land after destroy; the original swallowed this too.
      }
    }, 0);
  }
}
